import { useEffect, useState } from "react";
import { ListGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import Routes from "../../Constants/Routes";
import { getList } from "../../api/globalFunction";
import { apiEndpoints } from "../../api/apiEndpoints";
import { toast } from "react-toastify";
import { generateSlug } from "../../Utils/SeoUtils";

function SearchBar() {
    const [searchTerm, setSearchTerm] = useState("");
    const [showStaticSuggestions, setShowStaticSuggestions] = useState(false);
    const [expertData, setExpertData] = useState([]);
    const [centerData, setCenterData] = useState([]);
    const [diseasesData, setDiseasesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeItem, setActiveItem] = useState(null);

    // normalizeText
    const normalizeText = (text) => text?.toLowerCase().replace(/\./g, "").trim();

    const highlightMatch = (text) => {
        if (!searchTerm) return text;
        const normalizedText = normalizeText(text);
        const words = searchTerm
            .toLowerCase()
            .replace(/\./g, "")
            .split(" ")
            .map(word => word.trim())
            .filter(word => word.length > 0);
        const regex = new RegExp(`(${words.map(word => word.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")).join("|")})`, "gi");
        return normalizedText.replace(regex, "<mark>$1</mark>");
    };

    // Fetch Experts
    const fetchExperts = async () => {
        try {
            const response = await getList(apiEndpoints.Doctors.Public.alldoctorList);
            setExpertData(response?.doctorList ?? []);
        } catch (err) {
            toast.error("Failed to fetch doctor data.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch Centers
    const fetchCenter = async () => {
        try {
            const response = await getList(apiEndpoints.Clinic.Public.allClinicList);
            setCenterData(response?.cliniccenterList ?? []);
        } catch (err) {
            toast.error("Failed to fetch center data.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch Diseases
    const fetchDiseases = async () => {
        try {
            const response = await getList(
                `${apiEndpoints.Blogs.Public.blogList}?ModuleName=diseases`
            );
            setDiseasesData(response?.data ?? []);
        } catch (err) {
            toast.error("Failed to fetch diseases data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExperts();
        fetchCenter();
        fetchDiseases();
    }, []);

    // Filtering Data
    const filteredCenters = centerData.filter((item) => {
        const normalizedName = normalizeText(item.ClinicName);
        return searchTerm.toLowerCase().replace(/\./g, "").split(" ").every((word) => normalizedName.includes(word));
    });

    const filteredDiseases = diseasesData.filter((item) => {
        const normalizedName = normalizeText(item.Title);
        return searchTerm.toLowerCase().replace(/\./g, "").split(" ").every((word) => normalizedName.includes(word));
    });
    const filteredExperts = expertData.filter((item) => {
        const normalizedName = normalizeText(item.DoctorName);
        return searchTerm
            .toLowerCase()
            .replace(/\./g, "")
            .split(" ")
            .every((word) => normalizedName.includes(word));
    });

    return (
        <div className="navbar-search-bar">
            <input
                type="search"
                className="form-control"
                placeholder="Search Anything..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowStaticSuggestions(true)}
            onBlur={() => setTimeout(() => setShowStaticSuggestions(false), 300)}
            />
            <i className="bx bx-search"></i>
            {showStaticSuggestions && (
                <div className="suggestion-list">
                    {searchTerm.length > 0 && (
                        <>
                            {filteredExperts.length > 0 && (
                                <div className="dynamic-suggestions">
                                    <h6>Experts</h6>
                                    <ListGroup>
                                        {filteredExperts.map((item, index) => (
                                            <ListGroup.Item
                                                key={`expert-${index}`}
                                                as={Link}
                                                to={`${Routes.publicRoutes.DoctorDetail}/${generateSlug(item.DoctorName)}`}
                                                onMouseEnter={() => setActiveItem(`expert-${index}`)}
                                                onMouseLeave={() => setActiveItem(null)}
                                                className={activeItem === `expert-${index}` ? "active" : ""}
                                            >
                                                <span dangerouslySetInnerHTML={{ __html: highlightMatch(item.DoctorName) }} />
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </div>
                            )}
                            {filteredCenters.length > 0 && (
                                <div className="dynamic-suggestions">
                                    <h6>Centers</h6>
                                    <ListGroup>
                                        {filteredCenters.map((item, index) => (
                                            <ListGroup.Item
                                                key={`center-${index}`}
                                                as={Link}
                                                to={`${Routes.publicRoutes.ClinicDetails}/${generateSlug(item.ClinicName)}`}
                                                onMouseEnter={() => setActiveItem(`center-${index}`)}
                                                onMouseLeave={() => setActiveItem(null)}
                                                className={activeItem === `center-${index}` ? "active" : ""}
                                            >
                                                <span dangerouslySetInnerHTML={{ __html: highlightMatch(item.ClinicName) }} />
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </div>
                            )}
                            {filteredDiseases.length > 0 && (
                                <div className="dynamic-suggestions">
                                    <h6>Diseases</h6>
                                    <ListGroup>
                                        {filteredDiseases.map((item, index) => (
                                            <ListGroup.Item
                                                key={`disease-${index}`}
                                                as={Link}
                                                to={`${Routes.publicRoutes.DiseaseDetail}/${encodeURIComponent(item.Slug)}`}
                                                onMouseEnter={() => setActiveItem(`disease-${index}`)}
                                                onMouseLeave={() => setActiveItem(null)}
                                                className={activeItem === `disease-${index}` ? "active" : ""}
                                            >
                                                <span dangerouslySetInnerHTML={{ __html: highlightMatch(item.Title) }} />
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </div>
                            )}
                        </>
                    )}
                    <div className="quick-link">
                        <h6>Quick Link</h6>
                        <ListGroup>
                            {[
                                { name: "Kidney Failure", path: Routes.publicRoutes.KidneyFailure },
                                { name: "Pain management", path: Routes.publicRoutes.PainManagement },
                                { name: "Our Expert", path: Routes.publicRoutes.OurExperts },
                                { name: "Our Center", path: Routes.publicRoutes.OurCenters },
                                { name: "All Diseases", path: Routes.publicRoutes.Diseases }
                            ].map((link, index) => (
                                <ListGroup.Item
                                    key={index}
                                    as={Link}
                                    to={link.path}
                                    onMouseEnter={() => setActiveItem(`quick-${index}`)}
                                    onMouseLeave={() => setActiveItem(null)}
                                    className={activeItem === `quick-${index}` ? "active" : ""}
                                >
                                    <span dangerouslySetInnerHTML={{ __html: highlightMatch(link.name) }} />
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SearchBar;
